import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation } from '@apollo/client';

const ASSIGN_VIN_TO_DEALER = gql`
  mutation AssignVinToDealer($chassisNo: String!, $dealerCode: String!) {
    assignVinToDealer(chassisNo: $chassisNo, dealerCode: $dealerCode) {
      status
      message
    }
  }
`;

interface AssignVinDialogProps {
  vins: string[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignVinDialog: React.FC<AssignVinDialogProps> = ({ vins, open, onClose, onSuccess }) => {
  const [vin, setVin] = useState('');
  const [dealerCode, setDealerCode] = useState('');
  const [assignVinToDealer, { loading }] = useMutation(ASSIGN_VIN_TO_DEALER);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const handleSubmit = async () => {
    if (!vin.trim() || !dealerCode.trim()) {
      setAlert({
        open: true,
        message: 'Please enter both VIN and Dealer Code',
        severity: 'error'
      });
      return;
    }

    try {
      const { data } = await assignVinToDealer({
        variables: {
          chassisNo: vin.trim(),
          dealerCode: dealerCode.trim()
        }
      });

      if (data?.assignVinToDealer?.status) {
        setAlert({
          open: true,
          message: data.assignVinToDealer.message || 'VIN assigned to dealer successfully!',
          severity: 'success'
        });
        
        onSuccess();
        
        setTimeout(() => {
          onClose();
          setVin('');
          setDealerCode('');
        }, 2000);
      } else {
        throw new Error(data?.assignVinToDealer?.message || 'Assignment failed');
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message: error.message || 'Error assigning VIN to dealer',
        severity: 'error'
      });
    }
  };

  const handleCloseAlert = () => {
    setAlert(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: '#ffffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Assign VIN to Dealer
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#000' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent  sx={{ padding: '24px' }}>
          <Autocomplete
            options={vins}
            value={vin || null}
            onChange={(_, newValue) => setVin(newValue || '')}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="normal"
                label="VIN*"
              />
            )}
          />
          {/* <TextField
            fullWidth
            margin="normal"
            label="VIN*"
            value={vin}
            onChange={(e) => setVin(e.target.value)}
            disabled={loading}
            inputProps={{
              maxLength: 20,
              pattern: '[A-Za-z0-9]+',
              title: 'VIN should be alphanumeric and up to 20 characters'
            }}
          /> */}
          <TextField
            fullWidth
            margin="normal"
            label="Dealer Code*"
            value={dealerCode}
            onChange={(e) => setDealerCode(e.target.value)}
            disabled={loading}
            inputProps={{
              maxLength: 20,
              pattern: '[A-Za-z0-9]+',
              title: 'Dealer Code should be alphanumeric and up to 20 characters'
            }}
          />
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant='outlined'
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !vin.trim() || !dealerCode.trim()}
            variant="contained"
            color='error'
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'SUBMIT'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert?.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alert?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignVinDialog;